import type { VesselSpeedConsumptionFragment } from '../../graphql';
import { VesselSpeedType } from '../../graphql';
import type { Vessel } from './Vessel';
import type { CalculationConfig } from './ScheduleDataContext';
import type { CargoEvent, Events, TimeData } from './TimeData';
import {
  isCargoEventTimeData,
  getEventStartLocationId,
  isLoadEventTimeData,
  isDischargeEventTimeData,
} from './TimeData';
import {
  getSpeedConsumptionsForAreaAndBunker,
  getSpeedConsumptionsForTagAndType,
} from './Core.bunker-consumption';
import { computeTceForTimeData } from '../shipment/TCE';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { GetIncomeForLoadedAmount } from '../shipment/income/income';
import type { TceComputationOptions, TceResult } from '../shipment/TCE';
import { DeriveCapacitiesAndTimes } from './DeriveCapacitiesAndTimes';
import type { ScheduleDataContext } from './ScheduleDataContext';
import type { ClientId } from './ClientId';
import { getParetoFrontierBy, msToH } from '@seaber/common/lib/math';
import {
  assertedDefined,
  filteredMap,
  get,
  groupBySequence,
  NONE,
  roundToBucket,
} from '../../lib/util';
import { minBy, uniq } from 'lodash';
import { hToD, hToMs } from '../../lib/time';
import * as z from 'zod';
import { compact, DefaultMap } from '@seaber/common/lib/collections';
import type { CalculateTimeDataPnl } from '../pnl/ui/context';
import { getPnlProcessorVessel } from '../pnl/context/context';
import { getEventLocation } from './Core';

export const speedOptimizationTargetSchema = z.enum([
  'tce',
  'minimize_delays',
  'minimize_costs',
]);
export type SpeedOptimizationTarget = z.infer<
  typeof speedOptimizationTargetSchema
>;

export type OptimizationInputs = {
  /** What to optimize for */
  target: SpeedOptimizationTarget;
  /** Optimization scope */
  scope: {
    /** Optimize only these specific legs (identified by following port call). Use getOptimizableLegs to get this value. */
    portCallIds: Set<ClientId>;
  };
  /** Speed constraints */
  speedConstraints: {
    /** Minimum speed constraint (knots) */
    min?: number;
    /** Maximum speed constraint (knots) */
    max?: number;
  };
  /** Operation window buffers (hours) */
  operationBuffers: {
    /** Additional buffer for loading start relative to layday (positive = tighter loading window, negative = allow early) */
    loadingStart: number;
    /** Additional buffer for loading start relative to cancelling (positive = tighter loading window, negative = allow late) */
    loadingEnd: number;
    /** Additional buffer for discharge start relative to rta (positive = tighter discharge window, negative = allow early) */
    dischargingStart: number;
    /** Additional buffer for discharge start relative to deliberedBefore (positive = tighter discharge window, negative = allow late) */
    dischargingEnd: number;
  };
};

/**
 * System context and dependencies for speed optimization
 */
export type OptimizationContext = {
  /** Target vessel */
  vessel: Vessel;
  /** All vessel events */
  vesselEvents: Events[];
  /** Calculation config for bunker prices and other parameters */
  calculationConfig: CalculationConfig;
  /** Function to get income for loaded amounts (required for voyage optimization) */
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount;
  /** TCE computation options (required for voyage optimization) */
  tceOptions: Pick<
    TceComputationOptions,
    | 'euEtsCostEnabled'
    | 'fuelEuCostEnabled'
    | 'pnlData'
    | 'portCallCostSharingDetails'
  >;
  /** Map of orders by ID (required for voyage optimization) */
  orderById: Map<number, ScenarioOrder> | null;
  /** Schedule data context (required for voyage optimization) */
  scheduleDataContext: ScheduleDataContext;
  calculateTimeDataPnl: CalculateTimeDataPnl;
};

/** User inputs from modal (excludes scope which is determined by system context) */
export type OptimizationModalInputs = Omit<OptimizationInputs, 'scope'>;

export type SpeedOptimizationParams = OptimizationContext & OptimizationInputs;

export interface SpeedOptimizationResult {
  /** The optimal speed in knots */
  optimalSpeed: number;
  /** The optimized duration in hours */
  optimizedDuration: number;
  /** The estimated bunker consumption in metric tons */
  estimatedConsumption: number;
  /** The estimated cost in USD */
  estimatedCost: number;
  /** The estimated savings compared to original speed in USD */
  estimatedSavings?: number;
  /** Indicates if this is an override of the original speed */
  isOverride: boolean;
}

export interface VoyageSpeedOptimizationResult {
  /** Map of leg indices to optimization results */
  speedPlan: Map<number, SpeedOptimizationResult>;
  /** Optimized TCE in €/day */
  optimizedTCE: number;
  /** Original TCE before optimization */
  originalTCE: number;
  /** Number of iterations to convergence */
  iterations: number;
  /** Whether optimization converged */
  converged: boolean;
}

type OptimizableLeg = {
  etaIndex: number;
  etdIndex: number;
  distance: number;
  portCallId: ClientId | null;
  shipmentIds: Set<ClientId | null>;
  possibleSpeeds: number[];
};

/**
 * Identify ETA events that can be optimized within the shipment scope
 * Returns an array of legs=portcalls, not necessarily a continuous sequence
 */
export function getOptimizableLegs(
  vesselTimeData: readonly TimeData[],
  vessel: Vessel,
  portCallIds: Set<ClientId>,
  speedConstraints: { min?: number; max?: number },
): OptimizableLeg[] {
  let nextEtaIndex = 0;
  const allPortCalls = groupBySequence(vesselTimeData, (td) =>
    getEventStartLocationId(td.event),
  ).map((tds): OptimizableLeg => {
    /** Typically, but not necessarily a PortCallArrivalEventTimeData (if port call events have not been attached) */
    const etaTd = assertedDefined(tds.at(0), 'first event of port call');
    const shipmentIds = new Set(
      filteredMap(tds, (td) =>
        isCargoEventTimeData(td) ? td.event.order.shipmentId : NONE,
      ),
    );

    const insideEcaBunkerGradeId =
      etaTd.costs.bunkerGrade.insideEcaData?.gradeId;
    const outsideEcaBunkerGradeId =
      etaTd.costs.bunkerGrade.outsideEcaData?.gradeId;

    const [insideEcaConsumptions, outsideEcaConsumptions] = [
      insideEcaBunkerGradeId,
      outsideEcaBunkerGradeId,
    ].map((bunkerGradeId) => {
      const allNodes =
        bunkerGradeId &&
        getSpeedConsumptionsForTagAndType(
          'leg',
          etaTd.laden ? VesselSpeedType.LADEN : VesselSpeedType.BALLAST,
          vessel.speedConsumptions,
          vessel.speedConsumptionsByTag,
          bunkerGradeId,
        );
      return allNodes
        ? getSpeedConsumptionsForAreaAndBunker(
            allNodes,
            etaTd.area,
            bunkerGradeId,
          )
        : [];
    });

    const possibleSpeeds = getConstrainedSpeedsForLeg(
      compact([...insideEcaConsumptions, ...outsideEcaConsumptions]),
      speedConstraints,
    );

    const etaTdIndex = nextEtaIndex;
    nextEtaIndex += tds.length;
    const etdIndex = nextEtaIndex - 1;

    return {
      etaIndex: etaTdIndex,
      etdIndex,
      distance: etaTd.distance,
      portCallId: etaTd.event.portCallId,
      shipmentIds,
      possibleSpeeds,
    };
  });

  return allPortCalls.filter((pc) => {
    const isIncluded = !!pc.portCallId && portCallIds.has(pc.portCallId);
    const hasLeg = pc.distance > 0;
    return isIncluded && hasLeg;
  });
}

/** Extract all available vessel speeds for modal display */
export function getAllAvailableSpeeds(vessel: Vessel): number[] {
  const allSpeeds = new Set<number>();

  for (const [_speedType, areas] of vessel.speedConsumptions) {
    for (const [_area, consumptions] of areas) {
      for (const consumption of consumptions) {
        allSpeeds.add(+consumption.speed);
      }
    }
  }

  return Array.from(allSpeeds);
}

/** Returns available speeds for a leg, taking into account min/max constraints */
function getConstrainedSpeedsForLeg(
  consumptions: VesselSpeedConsumptionFragment[],
  constraints: { min?: number; max?: number },
): number[] {
  const allUniqueSpeeds = uniq(consumptions.map((c) => +c.speed));

  // Safety minimum of 1 (0 speeds have been observed in some data)
  const minSpeed = Math.max(1, constraints.min ?? 0);
  const maxSpeed = constraints.max ?? Infinity;

  return allUniqueSpeeds.filter(
    (speed) => speed >= minSpeed && speed <= maxSpeed,
  );
}

export type FromTo<T = number> = { from: T; to: T };

type OptimizeLegSpeedError = {
  type: 'error';
  error: Error;
};
type OptimizeLegSpeedSuccess = {
  type: 'success';
  timeData: TimeData[];
  path: Path;
  converged: boolean;
  changeSummary: {
    legDetails: Array<{
      origin: string;
      destination: string;
      speed: FromTo;
      duration: FromTo;
    }>;
    tce: FromTo<NonNullable<TceResult>>;
  };
};
type OptimizeLegSpeedResult = OptimizeLegSpeedError | OptimizeLegSpeedSuccess;

/**
 * Uses the Dinkelbach algorithm to optimize the speeds of the legs on a timeline.
 * On each iteration we assume a lambda = currently known best TCE and find the combination of speeds that
 * minimizes the cost of bunker + timeCost, where timeCost = lambda * time.
 * We repeat this process until the TCE converges to a stable value.
 *
 * Note: We could also solve the problem with a direct approach by substituting the cost simply by total cost.
 * However, it is assumed that with the Dinkelbach algorithm we are able to prune bad solutions early more consistently in typical voyages
 * (although the worst case complexity is still the same), and it has been working well with several sample voyages.
 */
export function optimizeVoyageLegSpeeds(
  params: SpeedOptimizationParams,
): OptimizeLegSpeedResult {
  const { calculationConfig, scheduleDataContext } = params;

  const getTimeData = (events: Events[]) =>
    DeriveCapacitiesAndTimes(
      params.vessel,
      events,
      scheduleDataContext,
      calculationConfig,
      {
        skipSwapComputation: true,
        calculateCosts: true,
      },
    ).timeData;

  const originalTimedata = getTimeData(params.vesselEvents);

  const legsToOptimize = getOptimizableLegs(
    originalTimedata,
    params.vessel,
    params.scope.portCallIds,
    params.speedConstraints,
  );

  if (legsToOptimize.length === 0) {
    return {
      type: 'error',
      error: new Error('optimizeVoyageLegSpeeds: no legs to optimize'),
    };
  }

  const voyageStartIndex = legsToOptimize[0].etaIndex;
  const timelineEndIndex = params.vesselEvents.length - 1;

  // Calculate baseline violations from original schedule for the vessel schedule starting from first leg to optimize
  const windowViolationsByOrder = calculateOperationViolations(
    originalTimedata,
    params.operationBuffers,
    voyageStartIndex,
    timelineEndIndex,
  );
  const violationLimits = getViolationStats(windowViolationsByOrder);

  // Average TCE is calculated for the whole vessel
  const tceOptions: TceComputationOptions = {
    ...params.tceOptions,
    firstIndex: 0,
    lastIndex: originalTimedata.length - 1,
    outgoingCoaId: null,
  };

  const getTce = (timeData: TimeData[]) => {
    const pnl = params.calculateTimeDataPnl(timeData, {
      type: 'vessel',
      vessel: getPnlProcessorVessel(params.vessel),
    });

    return computeTceForTimeData(
      timeData,
      null,
      params.getIncomeForLoadedAmount,
      scheduleDataContext.distances,
      tceOptions,
      params.orderById,
      pnl,
    );
  };

  // The full result used for comparison later, the tce value used as the initial target for the optimization
  const originalTceResult = getTce(originalTimedata);
  const originalTce = originalTceResult?.tce;
  if (!originalTce) {
    return {
      type: 'error',
      error: new Error(
        'optimizeVoyageLegSpeeds: could not calculate original TCE',
      ),
    };
  }

  // Initialize Dinkelbach algorithm
  let lambda = originalTce; // Start with current voyage TCE
  const epsilon = 0.01; // Convergence threshold for TCE, in practice we could always match by exact value instead of threshold because we use discrete speed values
  let iteration = 0;
  // The algorithm is expected to converge, but we'll use a safety net just to be sure, typically takes just 1 or a few iterations.
  const maxIterations = 20;
  let converged = false;

  let currentPath = null;

  while (iteration < maxIterations) {
    const result = shortestPath(
      params.vesselEvents,
      legsToOptimize,
      lambda,
      getTimeData,
      params.operationBuffers,
      violationLimits,
    );

    if (!result) {
      break;
    }

    const { path, events: iterationEvents } = result;

    const iterationTimeData = getTimeData(iterationEvents);
    const iterationTce = getTce(iterationTimeData)?.tce;
    if (!iterationTce) {
      break;
    }

    currentPath = path;

    if (Math.abs(iterationTce - lambda) < epsilon) {
      converged = true;
      break;
    }

    lambda = iterationTce;

    iteration++;
  }

  if (!currentPath) {
    return {
      type: 'error',
      error: new Error('optimizeVoyageLegSpeeds: no path found'),
    };
  }

  // Collect the changes for reporting purposes

  const finalEvents = applyEdgesToEvents(
    params.vesselEvents,
    currentPath.edges,
  );
  const finalTimeData = getTimeData(finalEvents);

  const legDetails = extractLegSpeedChanges(
    originalTimedata,
    finalTimeData,
    legsToOptimize,
    params.scheduleDataContext.locations,
  );

  const finalTceResult = getTce(finalTimeData);
  if (!finalTceResult) {
    return {
      type: 'error',
      error: new Error(
        'optimizeVoyageLegSpeeds: could not calculate final TCE',
      ),
    };
  }

  return {
    type: 'success',
    timeData: finalTimeData,
    path: currentPath,
    converged,
    changeSummary: {
      legDetails,
      tce: { from: originalTceResult, to: finalTceResult },
    },
  };
}

/** An edge in the graph of all possible speeds through the legs to optimize. The leg plus the following port call until ETD end */
type Edge = {
  leg: OptimizableLeg;
  event: Events;
  /** Edge cost (euros), but not to be confused with actual costs for the port call */
  cost: number;
  speed: number;
  /** In hours */
  duration: number;
};
type Path = {
  edges: Edge[];
  totalCost: number;
};
/** A node in the graph. Contains the selected Edges to reach this Node with the lowest cost. */
type Node = {
  bestPath: Path;
};

/** Apply a path to events, updating sea duration overrides for the legs.
 * Returns a new array of events, does not mutate the input.
 */
function applyEdgesToEvents(
  events: readonly Events[],
  edges: Edge[],
): Events[] {
  const newEvents = [...events];
  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];
    const leg = edge.leg;
    newEvents[leg.etaIndex] = edge.event;
  }
  return newEvents;
}

type OrderViolations = {
  loadingViolation: number;
  dischargingViolation: number;
};

type OrderViolationStats = {
  max: number;
  total: number;
};

function getViolationStats(
  violations: Map<number, OrderViolations>,
): OrderViolationStats {
  let max = 0;
  let total = 0;
  for (const {
    loadingViolation,
    dischargingViolation,
  } of violations.values()) {
    max = Math.max(max, loadingViolation, dischargingViolation);
    total += loadingViolation + dischargingViolation;
  }
  return { max, total };
}

/**
 * Calculate buffer violations for cargo events in the given time data range
 * TODO: Possible improvement: Currently only able to calculate relative to operations (load/discharge), the mode to calculate relative to the ETA should be supported later
 * @param buffers Additional buffers to adjust operation windows by
 * @param startIndex Start of the range, inclusive
 * @param endIndex End of the range, inclusive
 * @returns Violations by order id
 */
function calculateOperationViolations(
  timeData: TimeData[],
  buffers: OptimizationInputs['operationBuffers'],
  startIndex = 0,
  endIndex = timeData.length - 1,
): Map<number, OrderViolations> {
  const violations = new DefaultMap<number, OrderViolations>(() => ({
    loadingViolation: 0,
    dischargingViolation: 0,
  }));

  /** Sets the violation for the given order. Positive = violation */
  const setOrderViolation = (
    td: TimeData<CargoEvent>,
    windowStart: number | undefined,
    windowEnd: number | undefined,
    violation: keyof OrderViolations,
  ) => {
    const orderViolations = violations.get(td.event.order.id);
    const startViolation =
      windowStart !== undefined ? msToH(windowStart - td.start) : 0;
    const endViolation =
      windowEnd !== undefined ? msToH(td.end - windowEnd) : 0;

    orderViolations[violation] = Math.max(
      orderViolations[violation],
      startViolation,
      endViolation,
    );
  };

  for (let i = startIndex; i <= endIndex && i < timeData.length; i++) {
    const td = timeData[i];
    if (!isCargoEventTimeData(td)) {
      continue;
    }

    const order = td.event.order;

    if (isLoadEventTimeData(td)) {
      const { loadingWindow: window } = order;
      // Resolve the adjusted loading window
      const adjustedWindowStart =
        window.start !== undefined
          ? window.start + hToMs(buffers.loadingStart)
          : undefined;
      const adjustedWindowEnd =
        window.end !== undefined
          ? window.end - hToMs(buffers.loadingEnd)
          : undefined;

      setOrderViolation(
        td,
        adjustedWindowStart,
        adjustedWindowEnd,
        'loadingViolation',
      );
    } else if (isDischargeEventTimeData(td)) {
      const { dischargingWindow: window } = order;
      // Resolve the adjusted discharging window
      const adjustedWindowStart =
        window.start !== undefined
          ? window.start + hToMs(buffers.dischargingStart)
          : undefined;
      const adjustedWindowEnd =
        window.end !== undefined
          ? window.end - hToMs(buffers.dischargingEnd)
          : undefined;

      setOrderViolation(
        td,
        adjustedWindowStart,
        adjustedWindowEnd,
        'dischargingViolation',
      );
    }
  }

  return violations;
}

/**
 * Validates if cargo events in the given time data satisfy the operation buffer constraints
 * @param operationBuffers Additional buffers to adjust operation windows by
 * @param violationLimits Baseline violations to compare against
 * @param startIndex Start of the range (inclusive)
 * @param endIndex End of the range (inclusive)
 * @returns true iff all TimeData satisfy operation windows
 */
function validateOperationWindows(
  timeData: TimeData[],
  operationBuffers: OptimizationInputs['operationBuffers'],
  violationLimits: OrderViolationStats,
  startIndex = 0,
  endIndex = timeData.length - 1,
): boolean {
  const currentViolations = calculateOperationViolations(
    timeData,
    operationBuffers,
    startIndex,
    endIndex,
  );

  // Calculate current totals and check individual maximums
  let currentTotal = 0;
  for (const {
    loadingViolation,
    dischargingViolation,
  } of currentViolations.values()) {
    // Each individual violation must not exceed the maximum from baseline
    if (
      loadingViolation > violationLimits.max ||
      dischargingViolation > violationLimits.max
    ) {
      return false;
    }

    currentTotal += loadingViolation + dischargingViolation;
  }

  return currentTotal <= violationLimits.total;
}

/** Calculates the set of speeds that produces the lowest cost, assuming the given lambda.
 * Path refers to the set of selections through the directed graph of possible speed settings on each leg. */
function shortestPath(
  events: Events[],
  legsToOptimize: OptimizableLeg[],
  lambda: number,
  getTimeData: (events: Events[]) => TimeData[],
  operationBuffers: OptimizationInputs['operationBuffers'],
  violationLimits: OrderViolationStats,
): {
  path: Path;
  events: Events[];
} | null {
  /** At each leg=layer, a map from starting time (bucketed to 1h) to the node containing the best path.
   * A Node is a single speed setting on a single leg. */
  const nodeLayers = legsToOptimize.map((leg) => ({
    leg,
    layer: new Map<number, Node>(),
  }));

  const firstStart = events[legsToOptimize[0].etaIndex].start;
  const initialLayer = new Map([
    [firstStart, { bestPath: { edges: [], totalCost: 0 }, bestCost: 0 }],
  ]);

  for (let layerIndex = 0; layerIndex < nodeLayers.length; layerIndex++) {
    const leg = nodeLayers[layerIndex].leg;
    const nextLayer = nodeLayers[layerIndex].layer;

    // Does not exist for the first leg, use empty path for it.
    const previousLayer = nodeLayers[layerIndex - 1]?.layer ?? initialLayer;
    const allPreviousLayerNodes = [...previousLayer.entries()];

    /** To improve performance, we can drop all intermediate results that are both slower and more costly than some other.
     * This step is not necessary for the algorithm to work. */
    const optimalPreviousLayerNodes = getParetoFrontierBy(
      allPreviousLayerNodes,
      ([, node]) => -node.bestPath.totalCost,
      ([etdTime, _node]) => -etdTime,
    );

    for (const [_etdTime, node] of optimalPreviousLayerNodes) {
      const speedResults = filteredMap(leg.possibleSpeeds, (speed) => {
        const duration = leg.distance / speed;
        const edgeEvent: Events = {
          ...events[leg.etaIndex],
          seaDurationOverride: hToMs(duration),
        };
        const edge: Edge = {
          leg,
          event: edgeEvent,
          cost: 0, // to be calculated below
          speed,
          duration,
        };
        const edges = [...node.bestPath.edges, edge];

        const pathEvents = applyEdgesToEvents(events, edges);
        const pathTimeData = getTimeData(pathEvents);

        // Validate buffer constraints - skip this speed if it violates adjusted windows
        // We validate from the start of the voyage segment to the end of timeline
        const currentVoyageStartIndex = legsToOptimize[0].etaIndex;
        const timelineEndIndex = events.length - 1;

        if (
          !validateOperationWindows(
            pathTimeData,
            operationBuffers,
            violationLimits,
            currentVoyageStartIndex,
            timelineEndIndex,
          )
        ) {
          return NONE;
        }

        const legEtaTd = pathTimeData[leg.etaIndex];
        const legEtdTd = pathTimeData[leg.etdIndex];
        const portDuration = msToH(legEtdTd.end - legEtaTd.start);
        const totalDuration = portDuration + legEtaTd.hoursAtSea;
        const totalDurationD = hToD(totalDuration);
        edge.cost = legEtaTd.costs.bunkerGrade.total + lambda * totalDurationD;

        const path: Path = {
          edges,
          totalCost: node.bestPath.totalCost + edge.cost,
        };

        return [legEtdTd.end, path] as const;
      });

      // Add the paths to the next layer, using the best one in each 1h bucket
      for (const [etdTime, path] of speedResults) {
        const nextBucket = roundToBucket(etdTime, hToMs(1), 'floor');

        const existingNextNode = nextLayer.get(nextBucket);

        if (
          !existingNextNode ||
          existingNextNode.bestPath.totalCost > path.totalCost
        ) {
          nextLayer.set(nextBucket, {
            bestPath: path,
          });
        }
      }
    }
  }
  const bestNode = minBy(
    [...nodeLayers[nodeLayers.length - 1].layer.values()],
    (n) => n.bestPath.totalCost,
  );

  if (!bestNode) {
    return null;
  }

  return {
    path: bestNode.bestPath,
    events: applyEdgesToEvents(events, bestNode.bestPath.edges),
  };
}

/**
 * Extract changes between original and optimized time data for display
 */
export interface ExtractedOptimizationChanges {
  legChanges: Array<{
    origin: string;
    destination: string;
    originalSpeed: number;
    newSpeed: number;
    originalDuration: number;
    newDuration: number;
  }>;
}

/** Extract optimization changes by comparing original and optimized time data */
export function extractLegSpeedChanges(
  originalTimeData: TimeData[],
  optimizedTimeData: TimeData[],
  legs: OptimizableLeg[],
  locations: Map<number, ScenarioLocation>,
) {
  const legChanges = legs.map((leg) => {
    const originalTd = originalTimeData[leg.etaIndex];
    const optimizedTd = optimizedTimeData[leg.etaIndex];

    const originalSpeed = originalTd.distance / originalTd.hoursAtSea;
    const optimizedSpeed = optimizedTd.distance / optimizedTd.hoursAtSea;

    // Get location names from the leg event data
    const prevTd = get(originalTimeData, leg.etaIndex - 1);
    const originLocation = prevTd && getEventLocation(prevTd.event, locations);
    const destinationName = getEventLocation(originalTd.event, locations).name;

    return {
      origin: originLocation?.name ?? 'Unknown',
      destination: destinationName,
      speed: { from: originalSpeed, to: optimizedSpeed },
      duration: { from: originalTd.hoursAtSea, to: optimizedTd.hoursAtSea },
    };
  });

  return legChanges;
}
